import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { 
  ArrowLeft, 
  Plus, 
  List, 
  Users, 
  FolderKanban, 
  BarChart3,
  TrendingUp,
  Calendar,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import type { NavigationItem } from "../Navigation";

interface DemandDashboardProps {
  onNavigateBack: () => void;
  onNavigateToFeature: (feature: NavigationItem) => void;
}

export function DemandDashboard({ onNavigateBack, onNavigateToFeature }: DemandDashboardProps) {
  const stats = [
    {
      title: "Total Demands",
      value: "247",
      change: "+18%",
      trend: "up",
      icon: List
    },
    {
      title: "In Progress",
      value: "42",
      change: "+5",
      trend: "up",
      icon: Calendar
    },
    {
      title: "Completed This Month",
      value: "28",
      change: "+12%",
      trend: "up",
      icon: CheckCircle
    },
    {
      title: "Success Rate",
      value: "94.2%",
      change: "+2.1%",
      trend: "up",
      icon: TrendingUp
    }
  ];

  const recentDemands = [
    {
      id: "D-2024-001",
      title: "Customer Portal Enhancement",
      priority: "high" as const,
      status: "in-review" as const,
      assignee: "John Smith",
      date: "2024-01-16",
      progress: 75
    },
    {
      id: "D-2024-002",
      title: "Mobile App Performance Optimization",
      priority: "medium" as const,
      status: "in-progress" as const,
      assignee: "Sarah Johnson",
      date: "2024-01-15",
      progress: 45
    },
    {
      id: "D-2024-003",
      title: "Data Analytics Dashboard",
      priority: "high" as const,
      status: "completed" as const,
      assignee: "Mike Chen",
      date: "2024-01-14",
      progress: 100
    },
    {
      id: "D-2024-004",
      title: "Security Audit Implementation",
      priority: "critical" as const,
      status: "pending" as const,
      assignee: "Emma Wilson",
      date: "2024-01-13",
      progress: 0
    }
  ];

  const quickActions = [
    {
      title: "Create New Demand",
      description: "Submit a new demand request",
      icon: Plus,
      action: () => onNavigateToFeature("create-demand"),
      color: "bg-green-500"
    },
    {
      title: "View Demand Backlog",
      description: "Manage existing demands",
      icon: List,
      action: () => onNavigateToFeature("demand-backlog"),
      color: "bg-blue-500"
    },
    {
      title: "Board Governance",
      description: "Review governance items",
      icon: Users,
      action: () => onNavigateToFeature("board-governance"),
      color: "bg-purple-500"
    },
    {
      title: "Project Portfolio",
      description: "Monitor active projects",
      icon: FolderKanban,
      action: () => onNavigateToFeature("project-portfolio"),
      color: "bg-orange-500"
    }
  ];

  const getPriorityBadge = (priority: 'low' | 'medium' | 'high' | 'critical') => {
    const variants = {
      low: { variant: "outline" as const, color: "text-green-600" },
      medium: { variant: "secondary" as const, color: "text-yellow-600" },
      high: { variant: "default" as const, color: "text-orange-600" },
      critical: { variant: "destructive" as const, color: "text-red-600" }
    };
    
    return (
      <Badge variant={variants[priority].variant} className="capitalize">
        {priority}
      </Badge>
    );
  };

  const getStatusBadge = (status: 'pending' | 'in-progress' | 'in-review' | 'completed') => {
    const variants = {
      pending: { variant: "outline" as const, color: "text-gray-600" },
      'in-progress': { variant: "secondary" as const, color: "text-blue-600" },
      'in-review': { variant: "default" as const, color: "text-yellow-600" },
      completed: { variant: "default" as const, color: "text-green-600" }
    };
    
    return (
      <Badge variant={variants[status].variant} className="capitalize">
        {status.replace('-', ' ')}
      </Badge>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onNavigateBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Demand Management</h1>
          <p className="text-muted-foreground">Track and manage demand requests and projects</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  <span className={stat.trend === 'up' ? 'text-green-600' : 'text-red-600'}>
                    {stat.change}
                  </span>
                  {' '}from last month
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Access frequently used features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={action.action}
                  >
                    <div className={`${action.color} p-2 rounded-md text-white`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-sm">{action.title}</h3>
                      <p className="text-xs text-muted-foreground">{action.description}</p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Recent Demands */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Demands</CardTitle>
                <CardDescription>
                  Latest demand requests and their status
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => onNavigateToFeature("demand-backlog")}>
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentDemands.map((demand) => (
                  <div key={demand.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium">{demand.title}</h3>
                        {getPriorityBadge(demand.priority)}
                        {getStatusBadge(demand.status)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {demand.id} � Assigned to {demand.assignee}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Created: {new Date(demand.date).toLocaleDateString()}
                      </div>
                      {demand.progress > 0 && demand.progress < 100 && (
                        <div className="mt-2">
                          <Progress value={demand.progress} className="h-1" />
                          <div className="text-xs text-muted-foreground mt-1">
                            {demand.progress}% complete
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}